<script>
    import dressing from "@intellectability/platform-common";
    import {AutoUpdateInput, MegaPopup, Modal, toast} from "@intellectability/platform-common/sv";
    import {ModalBody, ModalFooter, Row, Col, Button} from "@sveltestrap/sveltestrap";
    import {createForm} from "svelte-forms-lib";
    import * as yup from "yup";
    import EntityChangeHistory from "./EntityChangeHistory.svelte";
    import {userSecurity} from "@intellectability/platform-common/sv/state";
    import {configCheck} from "@intellectability/platform-common/utils";

    let checkConfig = configCheck(userSecurity.applicationConfig);

    let caseNoteName = $derived(checkConfig("case_notes_name") || "Case Note");

    /** @import { Snippet } from 'svelte' */;

    /**
     * @typedef {Object} props
     * @property {function} [onopen]
     * @property {function} [onclose]
     * @property {string} [consId]
     * @property {string} [caseNoteId]
     * @property {number} [caseNoteConsId]
     * @property {boolean} [modalOpen]
     */
    /** @type {props} */
    let {
        onopen = null,
        onclose = null,
        caseNoteId = '0', // caseNoteId for editing an existing case note
        caseNoteConsId = undefined, // caseNoteConsId for creating a new case note
        modalOpen = $bindable(false),
    } = $props();

    let loading = $state(false);

    /** @type {CaseNoteListEntry} */ // @ts-ignore
    let caseNote = $state({});
    let caseNoteExists = $derived(caseNoteId && caseNoteId !== '0');
    
    let initialValues = $derived({
        id: caseNoteId + "",
        cons_id: caseNoteConsId + "",
        billable: caseNote?.billable ?? "",
        type: caseNote?.type ?? "",
        service: caseNote?.service ?? "",
        service_id: (caseNote?.service_id ?? "") + "",
        reason: caseNote?.reason ?? "",
        note_date: caseNote?.note_date ?? "",
        begin_time: caseNote?.begin_time ?? "",
        end_time: caseNote?.end_time ?? "",
        note: caseNote?.note ?? "",
        provider_signature: caseNote.provider_signature ?? "",
        provider_signature_name: caseNote.provider_signature_name ?? "",
    });

    let field_margin_classes = "mb-4 mb-lg-4";
    const show_floating_labels = true;

    let isSigned = $derived(caseNote?.provider_signature?.length > 0 || caseNote?.provider_signature_name?.length > 0);

    let logDisplayed = $state(false);

    /**
     * Transforms an array of objects into an array of {value, label} objects.
     * @param {Object[]} array - The input array of objects.
     * @param {string} valueKey - The key name to use for the value property.
     * @param {string} labelKey - The key name to use for the label property.
     * @returns {{value: string, label: string}[]} An array of objects with {value, label}.
     */
    function transformArrayForSelect(array, valueKey, labelKey) {
        return array.map(item => ({
            value: String(item[valueKey]),
            label: String(item[labelKey])
        }));
    }

    // for dropdowns
    let billableOptions = $state.raw([
        {label: "Yes", value: "y"},
        {label: "No", value: "n"}
    ]);
    let typeOptions = $state.raw([]);
    let serviceOptions = $state.raw([]);
    let reasonOptions = $state.raw([]);

    const form_props = {
        validationSchema: yup.object().shape({
            billable: yup.string().trim(),
            type: yup.string().trim().required(),
            service_id: yup.number().required(),
            reason: yup.string().trim().required(),
            note_date: yup.date().required(),
            begin_time: yup.string().trim().test(
                "begin-end-consistency",
                "If end time is set, begin time must be set and before end time",
                function (begin_time) {
                    const { end_time } = this.parent;
                    if (!end_time) return true; // No end_time, nothing to check
                    if (!begin_time) return false; // end_time set but not begin_time
                    const beginSmallerThanEnd = begin_time < String(end_time); // begin_time must be before end_time
                    return beginSmallerThanEnd;
                }
            ),
            end_time: yup.string().trim().test(
                "end-begin-consistency",
                "If begin time is set, end time must be set and after begin time",
                function (end_time) {
                    const { begin_time } = this.parent;
                    if (!begin_time) return true; // No begin_time, nothing to check
                    if (!end_time) return false; // begin_time set but not end_time
                    const endBiggerThanBegin = end_time > String(begin_time); // end_time must be after begin_time
                    return endBiggerThanBegin;
                }
            ),
            note: yup.string().trim().required(),
            provider_signature_name: yup.string().trim().required(),
            provider_signature: yup.string().trim().required()
        })
    };

    let formDisabledBySecurity = false;

    const {form, errors, handleSubmit, updateInitialValues} = 
        createForm({
            initialValues,
            validationSchema: form_props.validationSchema,
            onSubmit: () => on_submit(),
        });
    
    $effect(function () {
       updateInitialValues(initialValues);
    });

    $effect(function load() {
        if(Number($form.cons_id) > 0){
            const needed_initial_data = [
                dressing.get(`/people/services/${$form.cons_id}`).then(({data: response}) => {
                    const {success, data} = response;
                    if (success === true) {
                        const {services} = data;
                        if (Array.isArray(services) && services.length > 0) {
                            serviceOptions = transformArrayForSelect(services, "svc_key_num", "svc_name");
                        }
                        return true;
                    }
                    return false;
                }),
                dressing.get("/cm/case_notes/types").then(({data: response}) => {
                    const {success, data} = response;
                    if (success === true) {
                        typeOptions = transformArrayForSelect(data.types, "type", "type");
                        return true;
                    }
                    return false;
                }),
                dressing.get("/cm/case_notes/contact_reasons").then(({data: response}) => {
                    const {success, data} = response;
                    if (success === true) {
                        reasonOptions = transformArrayForSelect(data.reasons, "type", "type");
                        return true;
                    }
                    return false;
                }),

            ];

            Promise.allSettled(needed_initial_data).then(function (resolved) {
                //@ts-ignore
                const suceess = resolved.every(r => r.value === true);
                if (suceess) {
                    return loading = false;
                }
            });
        }
    });

    $effect(function () {
        if (modalOpen && caseNoteId && caseNoteId !== '0') {
            loading = true;
            dressing.get(`/cm/case_notes/${caseNoteId}`)
                .then(({data: response}) => {
                    const {success, data} = response;
                    if(data.case_note) {
                        caseNote = data.case_note;
                    } else {
                        // @ts-ignore
                        caseNote = {};
                    }
                    loading = false;
                })
                .catch(error => {
                        toast.error("Failed to load Case Note details.");
                        loading = false;
                    }
                );
        }else if(modalOpen && caseNoteConsId){
            // @ts-ignore
            caseNote = {};
            loading = false;
        }
    });

    function toggle_modal() {
        modalOpen = !modalOpen;
        if (modalOpen && typeof onopen === "function") {
            onopen();
        }
    }

    /**
     * Updates the end time to match the start time when the start time is changed.
     */
    function on_update_start_time() {
        $form.end_time = $form.begin_time;
    }

    function on_submit() {
        let url = `/cm/case_notes/create`;
        if( $form.id && $form.id !== '0') url = `/cm/case_notes/update`;
        dressing.post(url, {
            ...$form
        }).then(
            ({data:response}) => {
                const {success, data} = response;
                if (success === true) {
                    toggle_modal();
                    toast.success(`${caseNoteName} successfully created/updated.`);
                }
            }
        ).catch(
            (error) => {
                toast.error(`Error creating/updating ${caseNoteName}.`);
            }
        );
    }

    function displayLogs() {
        logDisplayed = !logDisplayed;
    }

    function closeCaseNoteModal() {
        caseNoteId = '0';
        caseNoteConsId = undefined;
        // @ts-ignore
        caseNote = {};
        if(typeof onclose === "function"){
            onclose();
        }
    }
</script>

<Modal bind:showModal={modalOpen} title={`${caseNoteName} Details`} size="xl" onclose={closeCaseNoteModal}>
    <ModalBody>
        <h3>{caseNote.cons_name}</h3>
        Use this form to record notes of interactions with a person served.
        <form onsubmit={handleSubmit} novalidate>
            {#key caseNote.id}
                <fieldset disabled={loading} id="loading-fieldset">
                    <Row class="">
                        <Col xs="12" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="billable"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.billable}
                                    entityID={caseNote.id}
                                    type="select"
                                    invalid={$errors.billable.length > 0}
                                    invalidMessage={`Please select if the ${caseNoteName} is billable`}
                                    optionsFrom={billableOptions}
                                    label="Billable?"/>
                        </Col>
                        <Col xs="12" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="type"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.type}
                                    entityID={caseNote.id}
                                    type="select"
                                    invalid={$errors.type.length > 0}
                                    invalidMessage="Please select the method of contact"
                                    optionsFrom={typeOptions}
                                    required={true}
                                    label="Method of Contact"/>
                        </Col>
                        <Col xs="12" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="service_id"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.service_id}
                                    entityID={caseNote.id}
                                    type="select"
                                    invalid={$errors.service_id.length > 0}
                                    invalidMessage="Please select the service"
                                    optionsFrom={serviceOptions}
                                    required={true}
                                    label={"Service"}/>
                        </Col>
                        <Col xs="12" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="reason"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.reason}
                                    entityID={caseNote.id}
                                    type="select"
                                    invalid={$errors.reason.length > 0}
                                    invalidMessage="Please select the reason for contact"
                                    optionsFrom={reasonOptions}
                                    required={true}
                                    label={"Reason for Contact"}/>
                        </Col>
                        <Col xs="12" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="note_date"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.note_date}
                                    invalid={$errors.note_date.length > 0}
                                    invalidMessage="Please provide a valid date"
                                    entityID={caseNote.id}
                                    type="date"
                                    required={true}
                                    label={`${caseNoteName} Date`}/>
                        </Col>
                        <Col xs="6" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="begin_time"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.begin_time}
                                    entityID={caseNote.id}
                                    type="time"
                                    invalid={$errors.begin_time.length > 0}
                                    invalidMessage={$errors.begin_time}
                                    onUpdate={on_update_start_time}
                                    label={"Start Time"}/>
                        </Col>
                        <Col xs="6" lg="4" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="end_time"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.end_time}
                                    entityID={caseNote.id}
                                    type="time"
                                    invalid={$errors.end_time.length > 0}
                                    invalidMessage={$errors.end_time}
                                    label={"End Time"}/>
                        </Col>
                    </Row>
                    <Row class="">
                        <Col xs="12" lg="12" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="note"
                                    loading={loading}
                                    disabled={formDisabledBySecurity}
                                    bind:defaultValue={$form.note}
                                    entityID={caseNote.id}
                                    type="textarea"
                                    invalid={$errors.note.length > 0}
                                    invalidMessage="Please provide a note"
                                    required={true}
                                    label={"Note"}/>
                        </Col>
                    </Row>
                    <Row class="">
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="provider_signature_name"
                                    loading={loading}
                                    disabled={formDisabledBySecurity || isSigned}
                                    bind:defaultValue={$form.provider_signature_name}
                                    entityID={caseNote.id}
                                    type="text"
                                    invalid={$errors.provider_signature_name.length > 0}
                                    invalidMessage="Please set the name of the signer"
                                    required={true}
                                    label={"Provider's Signature's Name"}/>
                        </Col>
                        <Col xs="12" lg="6" class={field_margin_classes}>
                            <AutoUpdateInput
                                    floatingLabel={show_floating_labels}
                                    fieldName="provider_signature"
                                    loading={loading}
                                    disabled={formDisabledBySecurity || isSigned}
                                    defaultValue={$form.provider_signature}
                                    entityID={caseNote.id}
                                    type="signature"
                                    onUpdate={(signature) => {
                                        $form.provider_signature = signature.value;
                                    }}
                                    required={true}
                                    invalid={$errors.provider_signature.length > 0}
                                    invalidMessage="Please sign this field"
                                    label={"Provider's signature"}/>
                        </Col>
                    </Row>
                    <Button color="primary" class="rounded-pill">Submit</Button>
                </fieldset>
            {/key}
        </form>
    </ModalBody>
    <ModalFooter class="justify-content-start">
        <div class="ms-2">
            {#if caseNoteExists}
                <button id="change_log_button" class="btn btn-primary btn-sm rounded-pill" onclick={displayLogs}>
                    {"Change Log"}
                </button>
            {/if}
            <MegaPopup
                bind:active={logDisplayed}
                for="change_log_button"
                placement="top-start">
                <div class="pt-2 px-3 rounded-2 bg-white">
                    <EntityChangeHistory entity={"case_note"} id={caseNote.id}></EntityChangeHistory>
                </div>
            </MegaPopup>
        </div>
    </ModalFooter>
</Modal>